import { InkStroke } from "./InkStroke";
import {
  useId,
  type PointerEvent,
  type RefObject,
  type WheelEvent,
} from "react";
import type { CanvasEditor, CanvasEditorSnapshot } from "../controller/types";
import type {
  CanvasEdge,
  JsonValue,
  CanvasNode,
  CanvasTool,
  EntityId,
  ReferenceTarget,
} from "../domain/types";
import type { CanvasAssetResolver } from "../references";
import { resolveCanvasEdgeStyle } from "../edgeStyle";
import {
  CanvasNodeView,
  type CanvasNodeViewProps,
} from "./CanvasNodeView";
import type { ReferenceResolutionState } from "./hooks";
import {
  normalizeGestureBounds,
  type CanvasGesture,
  type CanvasResizeHandle,
} from "./useCanvasInteractions";
import { useCanvasMessages } from "./i18n";

export interface CanvasSceneProps {
  editor: CanvasEditor;
  snapshot: CanvasEditorSnapshot;
  svgRef: RefObject<SVGSVGElement | null>;
  tool: CanvasTool;
  orderedVisibleNodes: readonly CanvasNode[];
  visibleEdges: readonly CanvasEdge[];
  nodeById: ReadonlyMap<EntityId, CanvasNode>;
  referenceState: ReferenceResolutionState;
  editingNodeId?: EntityId | undefined;
  creationProps: Record<string, JsonValue>;
  preview?: CanvasGesture | undefined;
  assetResolver?: CanvasAssetResolver | undefined;
  onPointerDown(event: PointerEvent<SVGSVGElement>): void;
  onPointerMove(event: PointerEvent<SVGSVGElement>): void;
  onPointerUp(event: PointerEvent<SVGSVGElement>): void;
  onPointerCancel(event: PointerEvent<SVGSVGElement>): void;
  onWheel(event: WheelEvent<SVGSVGElement>): void;
  onNodePointerDown(node: CanvasNode, event: PointerEvent<SVGGElement>): void;
  onEditDiagram?(node: CanvasNode): void;
  onNodeContextMenu: NonNullable<CanvasNodeViewProps["onContextMenu"]>;
  onNodeResizePointerDown(
    node: CanvasNode,
    handle: CanvasResizeHandle,
    event: PointerEvent<SVGCircleElement>,
  ): void;
  onNodeRotatePointerDown(
    node: CanvasNode,
    event: PointerEvent<SVGCircleElement>,
  ): void;
  onNodeResizeKeyDown: CanvasNodeViewProps["onResizeKeyDown"];
  onNodeRotateKeyDown: CanvasNodeViewProps["onRotateKeyDown"];
  onEdgeReconnectPointerDown(
    edgeId: EntityId,
    endpoint: "source" | "target",
    event: PointerEvent<SVGCircleElement>,
  ): void;
  onEditingNodeChange(id?: EntityId): void;
  onReferenceActivate?: ((target: ReferenceTarget) => void) | undefined;
}

export function CanvasScene({
  editor,
  snapshot,
  svgRef,
  tool,
  orderedVisibleNodes,
  visibleEdges,
  nodeById,
  referenceState,
  editingNodeId,
  creationProps,
  preview,
  assetResolver,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  onWheel,
  onNodePointerDown,
  onEditDiagram,
  onNodeContextMenu,
  onNodeResizePointerDown,
  onNodeRotatePointerDown,
  onNodeResizeKeyDown,
  onNodeRotateKeyDown,
  onEdgeReconnectPointerDown,
  onEditingNodeChange,
  onReferenceActivate,
}: CanvasSceneProps) {
  const messages = useCanvasMessages();
  const markerPrefix = `canvas-${useId().replaceAll(":", "")}`;
  const previewMarkerId = `${markerPrefix}-preview-arrow`;
  const zoom = snapshot.view.zoom;
  const transform = `translate(${snapshot.view.panX} ${snapshot.view.panY}) scale(${zoom})`;
  return (
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      role="group"
      aria-label={messages.sceneLabel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onWheel={onWheel}
      style={{
        display: "block",
        position: "absolute",
        inset: 0,
        touchAction: "none",
        cursor:
          tool === "hand"
            ? "grab"
            : tool === "select"
              ? "default"
              : "crosshair",
        backgroundImage:
          snapshot.canvas.settings.paper === "plain" ? "none" : snapshot.canvas.settings.paper === "ruled" ? "linear-gradient(to bottom, transparent 23px, #dce5ef 23px)" : "radial-gradient(circle, #cbd5e1 1px, transparent 1px)",
        backgroundColor: snapshot.canvas.settings.paper ? "#fffefb" : undefined,
        backgroundPosition: `${snapshot.view.panX}px ${snapshot.view.panY}px`,
        backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
      }}
    >
      <defs>
        <marker
          id={previewMarkerId}
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="7"
          markerHeight="7"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#6366f1" />
        </marker>
        {visibleEdges.flatMap((edge, index) => {
          const style = resolveCanvasEdgeStyle(edge);
          return (["start", "end"] as const).map((position) => (
            <marker
              key={`${edge.id}-${position}`}
              id={`${markerPrefix}-edge-${index}-${position}`}
              viewBox="0 0 10 10"
              refX={position === "start" ? 1 : 9}
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill={style.color} />
            </marker>
          ));
        })}
      </defs>
      <g transform={transform}>
        {visibleEdges.map((edge, edgeIndex) => {
          const source = nodeById.get(edge.sourceNodeId);
          const target = nodeById.get(edge.targetNodeId);
          if (source === undefined || target === undefined) {
            return null;
          }
          const selected = snapshot.selection.edgeIds.has(edge.id);
          const style = resolveCanvasEdgeStyle(edge);
          const sourceX = source.geometry.x + source.geometry.width / 2;
          const sourceY = source.geometry.y + source.geometry.height / 2;
          const targetX = target.geometry.x + target.geometry.width / 2;
          const targetY = target.geometry.y + target.geometry.height / 2;
          const selectEdge = () =>
            editor.setSelection({
              edgeIds: [edge.id],
              primaryId: edge.id,
            });
          return (
            <g
              key={edge.id}
              data-canvas-edge-id={edge.id}
              role="button"
              tabIndex={0}
              aria-label={edge.label ?? messages.edgeAccessibleLabel(edge.id)}
              aria-pressed={selected}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  selectEdge();
                }
              }}
            >
              {selected && (
                <line
                  x1={sourceX}
                  y1={sourceY}
                  x2={targetX}
                  y2={targetY}
                  stroke="#a5b4fc"
                  strokeWidth={style.width + 5}
                  vectorEffect="non-scaling-stroke"
                  aria-hidden="true"
                />
              )}
              <line
                x1={sourceX}
                y1={sourceY}
                x2={targetX}
                y2={targetY}
                stroke={style.color}
                strokeWidth={style.width}
                vectorEffect="non-scaling-stroke"
                strokeDasharray={style.dash === "dashed" ? "7 6" : undefined}
                markerStart={
                  style.arrowStart
                    ? `url(#${markerPrefix}-edge-${edgeIndex}-start)`
                    : undefined
                }
                markerEnd={
                  style.arrowEnd
                    ? `url(#${markerPrefix}-edge-${edgeIndex}-end)`
                    : undefined
                }
                onPointerDown={(event) => {
                  event.stopPropagation();
                  selectEdge();
                }}
                style={{ cursor: "pointer" }}
              />
              {selected &&
                !snapshot.readOnly &&
                (["source", "target"] as const).map((endpoint) => (
                  <circle
                    key={endpoint}
                    data-canvas-edge-endpoint={endpoint}
                    cx={endpoint === "source" ? sourceX : targetX}
                    cy={endpoint === "source" ? sourceY : targetY}
                    r={7 / zoom}
                    fill="#ffffff"
                    stroke="#4f46e5"
                    strokeWidth={2 / zoom}
                    role="button"
                    tabIndex={0}
                    aria-label={
                      endpoint === "source"
                        ? messages.reconnectStart
                        : messages.reconnectEnd
                    }
                    onPointerDown={(event) =>
                      onEdgeReconnectPointerDown(edge.id, endpoint, event)
                    }
                    style={{ cursor: "crosshair" }}
                  />
                ))}
              {edge.label !== undefined && (
                <text
                  x={(sourceX + targetX) / 2}
                  y={(sourceY + targetY) / 2 - 8}
                  textAnchor="middle"
                  fill="#475569"
                  fontSize={13 / zoom}
                >
                  {edge.label}
                </text>
              )}
            </g>
          );
        })}
        {orderedVisibleNodes.filter((node) => !(preview?.type === "erase" && preview.nodeIds.has(node.id))).map((node) => {
          const referenceKey =
            node.reference === undefined
              ? undefined
              : `${node.reference.kind}:${node.reference.id}`;
          return (
            <CanvasNodeView
              key={node.id}
              node={node}
              selected={snapshot.selection.nodeIds.has(node.id)}
              editing={editingNodeId === node.id}
              readOnly={snapshot.readOnly}
              referenceResolution={
                referenceKey === undefined
                  ? undefined
                  : referenceState.resolutions.get(referenceKey)
              }
              referencePending={
                referenceKey !== undefined &&
                referenceState.pendingKeys.has(referenceKey)
              }
              referenceResolverAvailable={referenceState.resolverAvailable}
              assetResolver={assetResolver}
              onPointerDown={onNodePointerDown}
              onContextMenu={onNodeContextMenu}
              onResizePointerDown={onNodeResizePointerDown}
              onRotatePointerDown={onNodeRotatePointerDown}
              onResizeKeyDown={onNodeResizeKeyDown}
              onRotateKeyDown={onNodeRotateKeyDown}
              onStartEditing={(target) => target.type === "diagram" ? onEditDiagram?.(target) : onEditingNodeChange(target.id)}
              onCancelEditing={() => onEditingNodeChange()}
              onCommitText={(target, text) => {
                const key = target.type === "shape" ? "label" : "text";
                editor.dispatch({
                  type: "update-node",
                  id: target.id,
                  patch: { props: { [key]: text } },
                });
                onEditingNodeChange();
              }}
              onReferenceActivate={
                onReferenceActivate === undefined
                  ? undefined
                  : (target) => {
                      if (target.reference !== undefined) {
                        onReferenceActivate(target.reference);
                      }
                    }
              }
            />
          );
        })}
        <CanvasGesturePreview
          creationProps={creationProps}
          preview={preview}
          zoom={zoom}
          nodeById={nodeById}
          previewMarkerId={previewMarkerId}
        />
      </g>
    </svg>
  );
}

function CanvasGesturePreview({
  creationProps,
  preview,
  zoom,
  nodeById,
  previewMarkerId,
}: {
  creationProps: Record<string, JsonValue>;
  preview?: CanvasGesture | undefined;
  zoom: number;
  nodeById: ReadonlyMap<EntityId, CanvasNode>;
  previewMarkerId: string;
}) {
  if (preview?.type === "create") {
    return (
      <rect
        {...normalizeGestureBounds(preview.start, preview.current)}
        rx={8}
        fill="rgba(99,102,241,.08)"
        stroke="#6366f1"
        strokeWidth={2 / zoom}
        strokeDasharray={`${6 / zoom} ${4 / zoom}`}
      />
    );
  }
  if (preview?.type === "marquee") {
    return (
      <rect
        {...normalizeGestureBounds(preview.start, preview.current, 0)}
        fill="rgba(99,102,241,.1)"
        stroke="#6366f1"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeDasharray="5 4"
        pointerEvents="none"
      />
    );
  }
  if (
    preview?.type === "strategy" &&
    preview.strategy.kind === "lasso" &&
    preview.points.length > 0
  ) {
    return (
      <polygon
        points={preview.points
          .map((point) => `${point.x},${point.y}`)
          .join(" ")}
        fill="rgba(20,184,166,.1)"
        stroke="#0f766e"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeDasharray="5 4"
        pointerEvents="none"
      />
    );
  }
  if (preview?.type === "drag" && preview.guides.length > 0) {
    return (
      <>
        {preview.guides.map((guide, index) =>
          guide.axis === "x" ? (
            <line
              key={`${guide.axis}-${guide.position}-${index}`}
              x1={guide.position}
              x2={guide.position}
              y1={-100_000}
              y2={100_000}
              stroke="#ec4899"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          ) : (
            <line
              key={`${guide.axis}-${guide.position}-${index}`}
              x1={-100_000}
              x2={100_000}
              y1={guide.position}
              y2={guide.position}
              stroke="#ec4899"
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
              pointerEvents="none"
            />
          ),
        )}
      </>
    );
  }
  if (preview?.type === "draw") {
    return <InkStroke points={preview.points} color={String(creationProps.stroke ?? "#172033")} width={Number(creationProps.strokeWidth ?? 3)} opacity={Number(creationProps.opacity ?? 1)} />;
  }
  if (preview?.type === "arrow") {
    const source = nodeById.get(preview.sourceId);
    if (source === undefined) return null;
    return (
      <line
        x1={source.geometry.x + source.geometry.width / 2}
        y1={source.geometry.y + source.geometry.height / 2}
        x2={preview.current.x}
        y2={preview.current.y}
        stroke={String(creationProps.color ?? "#64748b")}
        strokeWidth={Number(creationProps.width ?? 2) / zoom}
        strokeDasharray={creationProps.dash === "dashed" ? "7 6" : undefined}
        markerEnd={creationProps.arrowEnd === false ? undefined : `url(#${previewMarkerId})`}
      />
    );
  }
  if (preview?.type === "edge-reconnect") {
    return (
      <circle
        cx={preview.current.x}
        cy={preview.current.y}
        r={8 / zoom}
        fill="rgba(99,102,241,.2)"
        stroke="#4f46e5"
        strokeWidth={2 / zoom}
        pointerEvents="none"
      />
    );
  }
  return null;
}
